import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { apiPost } from "@/lib/shieldx";
import { cn } from "@/lib/utils";
import { Mail, Loader2, Sparkles, X, Send, RefreshCw } from "lucide-react";

export function SmartComposer({ open, onClose, entityType, entityId, to: toInit, context = "update", orgId }) {
  const [draft, setDraft] = useState({ to: toInit || "", from: "", subject: "", body_html: "" });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  const [ctx, setCtx] = useState(context);

  const generate = async () => {
    setLoading(true);
    try {
      const d = await apiPost("/super-admin/email/compose", { entity_type: entityType, entity_id: entityId, context: ctx, to: toInit });
      setDraft((p) => ({ ...p, ...d, to: d.to || p.to }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate draft"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setDraft({ to: toInit || "", from: "", subject: "", body_html: "" }); setPreview(false); setCtx(context); generate(); } /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;
  const doSend = async (test) => {
    if (!draft.to || !draft.subject || !draft.body_html) { toast.error("Recipient, subject and body are required"); return; }
    setSending(true);
    try {
      if (test) {
        await apiPost("/super-admin/email/send-test", { to: draft.to });
        toast.success("Test email accepted for delivery");
      } else {
        await apiPost("/super-admin/email/send", { to: draft.to, subject: draft.subject, body_html: draft.body_html, org_id: orgId, entity_id: entityId, reason: "Super Admin composed email" });
        toast.success("Email accepted for delivery"); onClose();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl" data-testid="smart-composer">
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-400" /><h3 className="font-display text-sm font-semibold text-slate-100">ShieldX Smart Email Composer</h3></div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">To</label>
              <input data-testid="composer-to" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" /></div>
            <div><label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Context</label>
              <select data-testid="composer-context" value={ctx} onChange={(e) => setCtx(e.target.value)} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50">
                {["update", "verification", "incident", "invitation", "report", "scorecard", "recovery", "custom"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
          </div>
          <div><label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">From</label>
            <input readOnly value={draft.from || "ShieldX <admin@vs-solutions.org>"} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 text-sm text-slate-400 outline-none" /></div>
          <div><label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Subject</label>
            <input data-testid="composer-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" /></div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Body</label>
              <button onClick={() => setPreview((p) => !p)} className="text-[11px] text-blue-400 hover:text-blue-300">{preview ? "Edit" : "Preview"}</button>
            </div>
            {preview ? (
              <div className="min-h-[180px] rounded-lg border border-slate-800 bg-white p-4 text-sm text-slate-800" dangerouslySetInnerHTML={{ __html: draft.body_html }} />
            ) : (
              <textarea data-testid="composer-body" value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} rows={9}
                className="w-full rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-xs text-slate-200 outline-none focus:border-blue-500/50" />
            )}
          </div>
          {loading && <p className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating draft from real entity data…</p>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 px-5 py-3.5">
          <button onClick={generate} disabled={loading} data-testid="composer-regenerate" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:border-blue-500/50 hover:text-blue-300 disabled:opacity-50"><RefreshCw className="h-3.5 w-3.5" /> Regenerate</button>
          <div className="flex gap-2">
            <button onClick={() => doSend(true)} disabled={sending} className="rounded-lg px-3 py-2 text-xs text-slate-400 hover:text-slate-200">Send Test</button>
            <button onClick={() => doSend(false)} disabled={sending} data-testid="composer-send" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send</button>
          </div>
        </div>
      </motion.div>
    </div>, document.body);
}

export function EmailLink({ email, entityType, entityId, context, orgId, className }) {
  const [open, setOpen] = useState(false);
  if (!email) return <span className="text-slate-500">—</span>;
  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid="email-link"
        className={cn("inline-flex items-center gap-1 font-mono text-xs text-blue-400 hover:text-blue-300 hover:underline", className)}>
        <Mail className="h-3 w-3" /> {email}
      </button>
      <SmartComposer open={open} onClose={() => setOpen(false)} to={email} entityType={entityType} entityId={entityId} context={context} orgId={orgId} />
    </>
  );
}
